import { useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useInstructorWorkspace } from '../../../hooks/useLmsData';
import {
  WorkspaceEmptyState,
  WorkspacePageError,
  WorkspacePageLoader,
  WorkspaceSection,
  WorkspaceShell,
  WorkspaceStat,
  WorkspaceStats,
  WorkspaceStatusChip,
} from '../../ui/Workspace';

const InstructorDashboard = () => {
  const { currentUser } = useAuth();
  const { data, loading, error } = useInstructorWorkspace(currentUser?.uid);

  const courses = useMemo(() => data?.courses || [], [data]);
  const enrollments = useMemo(() => data?.enrollments || [], [data]);

  const stats = useMemo(() => {
    const activeEnrollments = enrollments.filter(
      (enrollment) =>
        enrollment.status === 'active' &&
        courses.some((course) => course.id === enrollment.courseId),
    );

    return {
      courses: courses.length,
      students: new Set(activeEnrollments.map((item) => item.studentId)).size,
      lessons: courses.reduce(
        (sum, course) => sum + (course.lessons?.length || 0),
        0,
      ),
    };
  }, [courses, enrollments]);

  if (loading) return <WorkspacePageLoader />;
  if (error) return <WorkspacePageError>{error.message}</WorkspacePageError>;

  return (
    <WorkspaceShell
      title="Instructor Dashboard"
      description="Your assigned courses and active learners."
    >
      <WorkspaceStats>
        <WorkspaceStat label="Courses" value={stats.courses} status="covered" />
        <WorkspaceStat label="Students" value={stats.students} status="planning" />
        <WorkspaceStat label="Lessons" value={stats.lessons} />
      </WorkspaceStats>

      <WorkspaceSection title="Your courses" status="covered" statusLabel={`${courses.length} active`}>
        {courses.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {courses.map((course) => (
              <div
                key={course.id}
                className="rounded-[16px] border border-stone-200 bg-stone-50/60 px-4 py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-stone-950">
                      {course.name || course.title || 'Course'}
                    </p>
                    <p className="mt-0.5 text-xs text-stone-500">
                      {course.courseCode || 'No course code'}
                    </p>
                  </div>
                  <WorkspaceStatusChip status={course.status || 'active'} />
                </div>
                <p className="mt-2 text-xs text-stone-600">
                  {course.lessons?.length || 0} lessons
                </p>
              </div>
            ))}
          </div>
        ) : (
          <WorkspaceEmptyState
            title="No courses assigned"
            message="An administrator can assign you to courses from Staff Management."
          />
        )}
      </WorkspaceSection>
    </WorkspaceShell>
  );
};

export default InstructorDashboard;
